import { AgentService, ReadLayer } from "@adfenix/types";
import { useQuery } from "@tanstack/react-query";
import { ReadLayerQueryFilter, searchReadLayer } from "api/services";

const getOfficesForAgent = async (
  hqId: string,
  filters: ReadLayerQueryFilter[]
) =>
  searchReadLayer<ReadLayer.OfficeSearchModel>({
    index: "office",
    service: "read-layer",
    sortOnColumn: "displayName",
    sortAscending: true,
    searchTermColumns: ["displayName"],
    pageSize: 500,
    filters: [
      {
        column: "hq.id",
        value: hqId
      },
      ...filters
    ]
  });

/**
 * Two calls to the read layer.
 * One for the agent's offices that are set in agent-service.
 * Another for the rest of the offices on the hq.
 *
 * The reason for this is that we always want to display the agent's offices no matter the
 * status but for the rest of the offices we only want to display the active ones.
 */
export const useAgentOffices = (
  hqId: string | undefined,
  agentData: AgentService.AgentModel | undefined
) => {
  const { data: allOffices } = useQuery(
    ["offices", agentData?.id, hqId],
    () =>
      getOfficesForAgent(hqId!, [
        {
          column: "active",
          value: "true"
        }
      ]),
    {
      enabled: Boolean(hqId),
      select: data => data?.documents
    }
  );

  const { data: agentOffices } = useQuery(
    ["agentOffices", agentData?.id, hqId],
    () =>
      getOfficesForAgent(
        hqId!,
        agentData!.officeIds.map(x => ({
          column: "id",
          value: x
        }))
      ),
    {
      enabled: Boolean(hqId && agentData && agentData.officeIds.length > 0),
      select: data => data?.documents
    }
  );

  const otherOfficesOnHq = allOffices?.filter(hqOffice =>
    agentOffices?.every(agentOffice => agentOffice.id !== hqOffice.id)
  );

  return {
    agentOffices,
    otherOfficesOnHq
  };
};
